<template>
  <div class="p-6">
    <h2 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-6">
      {{ website ? 'Edit Website' : 'New Website' }}
    </h2>

    <form @submit.prevent="handleSubmit" class="space-y-4">
      <div class="rounded-md border border-primary-200 dark:border-primary-800 bg-primary-50 dark:bg-primary-900/20 px-4 py-3">
        <p class="text-sm text-primary-900 dark:text-primary-100">
          Websites are used for sitemap imports, page inventory, audits, and optimization. The website URL from business setup is auto-added when possible.
        </p>
      </div>

      <UiControl id="website-name" label="Name *">
        <UiInput v-model="form.name" placeholder="My Website" />
      </UiControl>

      <UiControl id="website-domain" label="Domain *" hint="Enter a domain such as example.com. Full URLs are converted before saving.">
        <UiInput v-model="form.domain" placeholder="example.com" />
      </UiControl>

      <UiControl id="website-subdomain" label="Subdomain">
        <UiInput v-model="form.subdomain" placeholder="www" />
      </UiControl>

      <div v-if="errors" class="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
        <p class="text-sm text-red-700 dark:text-red-400">
          {{ errors }}
        </p>
      </div>

      <div class="flex justify-end space-x-3 pt-4 border-t dark:border-gray-700">
        <button
          type="button"
          @click="$emit('on-close-panel')"
          class="btn btn-secondary btn-sm">
          Cancel
        </button>
        <button
          type="submit"
          :disabled="saving"
          class="btn btn-primary btn-sm">
          {{ saving ? 'Saving...' : (website ? 'Update' : 'Create') }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { createWebsite, updateWebsite } from '~/javascript/api/websites.js'
import UiInput from '~/javascript/components/ui/form/input/input.vue'
import UiControl from '~/javascript/components/ui/form/control/control.vue'

const props = defineProps({
  website: { type: Object, default: null },
  businessId: { type: String, required: true }
})

const emit = defineEmits(['on-close-panel', 'saved'])

const saving = ref(false)
const errors = ref(null)

const defaultForm = { name: '', domain: '', subdomain: '' }
const form = ref({ ...defaultForm })

function normalizeDomainFields() {
  const value = String(form.value.domain || '').trim()
  if (!value) return

  try {
    const url = new URL(value.includes('://') ? value : `https://${value}`)
    const host = url.hostname.toLowerCase()
    if (host.startsWith('www.')) {
      form.value.domain = host.replace(/^www\./, '')
      if (!form.value.subdomain) form.value.subdomain = 'www'
    } else {
      form.value.domain = host
    }
  } catch {
    form.value.domain = value
  }
}

const handleSubmit = async() => {
  try {
    saving.value = true
    errors.value = null
    normalizeDomainFields()

    if (props.website) {
      await updateWebsite(props.website.id, { website: form.value })
    } else {
      await createWebsite(props.businessId, { website: form.value })
    }
    emit('saved')
  } catch (error) {
    if (error.response?.data?.details) {
      errors.value = Object.values(error.response.data.details).flat().join(', ')
    } else {
      errors.value = 'An error occurred. Please try again.'
    }
  } finally {
    saving.value = false
  }
}

onMounted(() => {
  if (props.website) {
    Object.keys(defaultForm).forEach((key) => {
      form.value[key] = props.website[key] || ''
    })
  }
})
</script>
